import { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useWaiting } from '../../context/waiting/WaitingContext';
import WaitingHeader from './sections/WaitingHeader';
import WaitingStepsCard from './sections/WaitingStepsCard';
import WaitingCallout from './sections/WaitingCallout';
import WaitingFooter from './sections/WaitingFooter';

const WaitingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkApprovalStatus } = useWaiting();
  const isCheckingRef = useRef(false);

  const checkStatus = useCallback(async () => {
    if (isCheckingRef.current) return;
    const token = localStorage.getItem('token_agents');
    if (token) {
      navigate('/dashboard');
      return;
    }

    const identifier =
      localStorage.getItem('pending_agent_identifier') || location.state?.identifier;
    if (!identifier) {
      const isWaitingState = location.state?.isWaiting;
      if (!isWaitingState) {
        navigate('/login');
      }
      return;
    }

    try {
      isCheckingRef.current = true;
      const res = await checkApprovalStatus(identifier);
      if (res?.isApproved && res?.token) {
        const tokenPayload = JSON.parse(atob(res.token.split('.')[1]));
        localStorage.setItem('token_agents', res.token);
        localStorage.setItem(
          'agent_id',
          res.agent_id || tokenPayload.agent_id || tokenPayload.business_id
        );
        localStorage.setItem('user_id', res.user?._id || tokenPayload._id);
        localStorage.setItem(
          'sub_user_role',
          res.sub_user_role || tokenPayload.sub_user_role || 'admin'
        );
        localStorage.setItem('agent_active_tab', 'home');
        localStorage.removeItem('pending_agent_identifier');

        toast.success('Your account has been approved! Welcome to Trrip.');

        if (res.needsProfiling) {
          localStorage.setItem('needs_profiling', 'true');
          setTimeout(() => {
            navigate('/dashboard', {
              state: {
                showProfiling: true,
                agentId: tokenPayload.business_id,
                companyName: res.user?.name
              }
            });
          }, 500);
        } else {
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 500);
        }
      }
    } catch (error) {
      console.warn('[WaitingPage] Status check error:', error.message);
    } finally {
      isCheckingRef.current = false;
    }
  }, [navigate, location.state]);

  useEffect(() => {
    // Immediate check on mount / reload
    checkStatus();

    // Realtime polling every 6 seconds while on waiting page
    const pollInterval = setInterval(() => {
      checkStatus();
    }, 6000);

    return () => clearInterval(pollInterval);
  }, [checkStatus]);

  return (
    <div className="min-h-screen w-full font-sans bg-[#FAFAFA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="max-w-[640px] w-full"
      >
        <WaitingHeader />
        <WaitingStepsCard />
        <WaitingCallout />
        <WaitingFooter />
      </motion.div>
    </div>
  );
};

export default WaitingPage;
